'use client';
import React from 'react';
import Image from 'next/image';
import { TeamMembersBlock as ITeamMembersBlock } from '../types';
import LinkedInIcon from '@/assets/linkedin.svg';
import Link from 'next/link';
import SmallRightArrowIcon from '@/assets/small_right_arrow.svg';
import { SupportedLanguages } from '@/types/globals';

export default function TeamMembersBlock({
  block,
  locale,
}: {
  block: ITeamMembersBlock;
  locale: SupportedLanguages;
}) {
  const isEnglish = locale === 'en';
  return (
    <div className="px-4 py-8">
      {/* Section Header */}
      {(block.pre_title || block.title || block.content || block.button) && (
        <div className="mx-auto flex max-w-6xl flex-col gap-4 md:flex-row md:items-start md:justify-between">
          {/* Left: Pre-title and Title */}
          <div className="w-full md:w-1/2">
            {block.pre_title && (
              <div className="font-source-sans-pro text-qf-purple w-fit rounded text-[14px] leading-[24px] font-semibold tracking-[10%] uppercase">
                {block.pre_title}
              </div>
            )}
            {block.title && (
              <h2 className="font-source-serif-pro pt-4 text-[32px] leading-[130%] font-semibold tracking-[-1.4%] text-gray-900">
                {block.title}
              </h2>
            )}
          </div>
          {/* Right: Content and Button */}
          <div className="mb-10 flex w-full flex-col items-start gap-4 md:w-1/2 md:items-start">
            {block.content && (
              <div className="font-source-sans-pro max-w-xl text-left text-[17px] leading-[26px] font-normal text-gray-600">
                {block.content}
              </div>
            )}
            {block.button && block.button.text && block.button.url && (
              <Link
                href={block.button.url}
                className="border-qf-purple/30 text-qf-purple hover:bg-qf-purple inline-flex h-[48px] w-fit min-w-[184px] items-center justify-center gap-[10px] rounded border px-[44px] py-[10px] text-center font-bold whitespace-nowrap transition hover:text-white"
              >
                {block.button.text} <SmallRightArrowIcon />
              </Link>
            )}
          </div>
        </div>
      )}
      {/* Team Grid */}
      <div className="mx-auto flex max-w-7xl flex-wrap justify-center gap-6">
        {block.team_members?.length
          ? block.team_members.map(member => (
              <div
                key={member.id}
                className="flex h-[442px] w-[360px] flex-col items-start rounded-2xl bg-white"
              >
                <div className="relative mb-4 h-[340px] w-[340px] overflow-hidden rounded-2xl">
                  {member.profile_picture && (
                    <Image
                      src={member.profile_picture.url}
                      alt={member.profile_picture.alt}
                      fill
                      className="object-cover"
                      sizes="360px"
                    />
                  )}
                </div>
                <div className="flex w-full items-center justify-between px-4">
                  <span className="font-source-serif-pro text-[20px] leading-[130%] font-normal tracking-[-1.4%] text-gray-900">
                    {member.title}
                  </span>
                  {member.linkedin_url && (
                    <button
                      onClick={() => window.open(member.linkedin_url, '_blank')}
                    >
                      <LinkedInIcon />
                    </button>
                  )}
                </div>
                <div className="px-4 pt-2 pb-8">
                  <span className="font-source-sans-pro text-[16px] leading-[24px] font-normal text-gray-600">
                    {isEnglish ? member.position : member.fr_position || ''}
                  </span>
                </div>
              </div>
            ))
          : null}
      </div>
    </div>
  );
}
